import {
  LEGACY_TRACK_CATALOG,
  getLegacyTrackDefinition,
  type LegacyTrackDefinition,
  type LegacyTrackId,
  type LegacyTrackPoint,
} from "./legacyTrackCatalog";
import {
  CUSTOM_TRACK_METRES_PER_UNIT,
  prepareTrackLayoutGeometry,
  type CustomTrackLayout,
  type CustomLayoutBridge,
  type CustomLayoutSample,
} from "./simulation/customTrackLayout";
import { normalizeCustomTrackDefinition, type CustomTrackDefinition } from "./simulation/customTrackSchema";

/** A v1 checkpoint gate, retained in the source coordinate units. */
export interface LegacyLayoutGate {
  readonly index: number;
  readonly x: number;
  readonly y: number;
  readonly nx: number;
  readonly ny: number;
}

/** A jump marker with both the source index and scaled layout distance. */
export interface LegacyLayoutJump {
  /** Source `buildTrack` field name. */
  readonly i: number;
  readonly progress: number;
  readonly distance: number;
}

/**
 * A tuple tunnel from `TRACK_DEFS` mapped to an object only at this adapter
 * boundary.  `u0/u1` are the original authored fractions; `i0/i1` are the
 * exact `Math.round(u * sampleCount) % sampleCount` source indices.
 */
export interface LegacyLayoutTunnel {
  readonly u0: number;
  readonly u1: number;
  readonly i0: number;
  readonly i1: number;
  readonly startDistance: number;
  readonly endDistance: number;
}

export interface LegacyTrackLayout {
  readonly id: LegacyTrackId;
  readonly name: string;
  readonly definition: LegacyTrackDefinition;
  /** Smoothed and start-rotated points, still in v1 source units. */
  readonly points: readonly LegacyTrackPoint[];
  /** Scaled samples, including the exact closing sample. */
  readonly samples: readonly CustomLayoutSample[];
  /** Planar v1 centreline length, scaled by 0.125 metres per source unit. */
  readonly length: number;
  readonly halfWidth: number;
  readonly bridges: readonly CustomLayoutBridge[];
  readonly checkpoints: readonly number[];
  readonly gates: readonly LegacyLayoutGate[];
  readonly jumps: readonly LegacyLayoutJump[];
  readonly tunnels: readonly LegacyLayoutTunnel[];
  /** Six source cluster centres after the bridge-avoidance walk. */
  readonly itemProgress: readonly number[];
}

function markerIndex(progress: number, sampleCount: number): number {
  const index = Math.round(Number(progress) * sampleCount) % sampleCount;
  if (!Number.isFinite(index)) throw new Error("Legacy track marker progress must be finite");
  return index;
}

function sourceDefinition(track: LegacyTrackId | string | LegacyTrackDefinition): LegacyTrackDefinition {
  if (typeof track !== "string") return track;
  const definition = getLegacyTrackDefinition(track);
  if (!definition) throw new Error(`Unknown legacy track: ${track}`);
  return definition;
}

function circularSeparation(a: number, b: number, count: number): number {
  const delta = Math.abs(a - b);
  return Math.min(delta, count - delta);
}

/** Port the v1 six-cluster / three-lane item placement rule by source index. */
function legacyItemProgress(points: readonly LegacyTrackPoint[], bridges: readonly CustomLayoutBridge[]): readonly number[] {
  const count = points.length;
  const inBridgeZone = (index: number) => bridges.some((bridge) =>
    circularSeparation(index, bridge.overIndex, count) < bridge.half + 12
    || circularSeparation(index, bridge.underIndex, count) < bridge.half + 12);
  return Object.freeze(Array.from({ length: 6 }, (_, cluster) => {
    let index = Math.floor(cluster * count / 6), guard = 0;
    while (inBridgeZone(index) && guard++ < count) index = (index + 4) % count;
    return index / count;
  }));
}

function freezeCustomDefinition(definition: CustomTrackDefinition): CustomTrackDefinition {
  for (const point of definition.raw) Object.freeze(point);
  Object.freeze(definition.raw);
  if (definition.jumps) Object.freeze(definition.jumps);
  if (definition.tunnels) {
    for (const tunnel of definition.tunnels) Object.freeze(tunnel);
    Object.freeze(definition.tunnels);
  }
  if (definition.items) Object.freeze(definition.items);
  if (definition.dips) Object.freeze(definition.dips);
  if (definition.tint) Object.freeze(definition.tint);
  return Object.freeze(definition);
}

function physicalSamples(samples: readonly CustomLayoutSample[]): { samples: readonly CustomLayoutSample[]; length: number } {
  let length = 0;
  const result = samples.map((sample, index) => {
    if (index) {
      const previous = samples[index - 1];
      length += Math.hypot(sample.x - previous.x, sample.y - previous.y, sample.z - previous.z);
    }
    return { ...sample, distance: length };
  });
  return { samples: Object.freeze(result), length };
}

/**
 * Prepare one v1 built-in without registering it with v2.
 *
 * The explicit `resampleByArc: false` mode is the important compatibility
 * boundary: `shared/sim.js` smooths built-ins and then rotates them, but only
 * editor `CUSTOM_DEFS` receive the subsequent arc-length resampling pass.
 * Tunnel tuples are also intentionally converted here rather than weakening
 * the v2 custom document schema to accept two unrelated wire shapes.
 */
export function buildLegacyTrackLayout(track: LegacyTrackId | string | LegacyTrackDefinition): LegacyTrackLayout {
  const definition = sourceDefinition(track);
  const geometry = prepareTrackLayoutGeometry(definition.raw, definition.width, {
    resampleByArc: false,
    includeElevationInDistance: false,
  });
  const sampleCount = geometry.points.length;
  const checkpoints = Object.freeze(Array.from({ length: 8 }, (_, index) => Math.floor(index * sampleCount / 8)));
  const gates = Object.freeze(checkpoints.map((index) => {
    const a = geometry.points[index];
    const b = geometry.points[(index + 2) % sampleCount];
    const fx = b[0] - a[0], fy = b[1] - a[1], length = Math.hypot(fx, fy) || 1;
    return { index, x: a[0], y: a[1], nx: fx / length, ny: fy / length };
  }));
  const jumps = Object.freeze(definition.jumps.map((progress) => {
    const index = markerIndex(progress, sampleCount);
    return { i: index, progress, distance: geometry.samples[index].distance };
  }));
  const tunnels = Object.freeze(definition.tunnels.map(([u0, u1]) => {
    const i0 = markerIndex(u0, sampleCount), i1 = markerIndex(u1, sampleCount);
    return { u0, u1, i0, i1, startDistance: geometry.samples[i0].distance, endDistance: geometry.samples[i1].distance };
  }));
  const itemProgress = legacyItemProgress(geometry.points, geometry.bridges);
  return Object.freeze({
    id: definition.id as LegacyTrackId,
    name: definition.name,
    definition,
    points: geometry.points,
    samples: geometry.samples,
    length: geometry.length,
    halfWidth: definition.width * CUSTOM_TRACK_METRES_PER_UNIT,
    bridges: geometry.bridges,
    checkpoints,
    gates,
    jumps,
    tunnels,
    itemProgress,
  });
}

/** Build all thirteen layouts in the catalog's original declaration order. */
export function buildLegacyTrackLayouts(): readonly LegacyTrackLayout[] {
  return Object.freeze(LEGACY_TRACK_CATALOG.map((definition) => buildLegacyTrackLayout(definition)));
}

/**
 * Build the source-faithful layout shape consumed by the v2 custom-runtime
 * preparation code, without registering a track or inventing a `c_` id.
 *
 * The detached layout above intentionally reports the planar v1 length.  A
 * playable classic runtime needs the true 3D arc length so bridge samples do
 * not compress distance; only the returned samples/length are changed here.
 * The definition is normalized through the v2 boundary, with tuple tunnels
 * converted to `{u0,u1}` objects locally and all source arrays cloned.
 */
export function buildClassicRuntimeLayout(id: string): CustomTrackLayout {
  const detached = buildLegacyTrackLayout(id);
  const definition = freezeCustomDefinition(normalizeCustomTrackDefinition({
    name: detached.name,
    raw: detached.definition.raw,
    width: detached.definition.width,
    themeKey: detached.definition.theme.themeKey,
    floorKey: detached.definition.theme.floorKey,
    propsKey: detached.definition.theme.propsKey,
    jumps: detached.definition.jumps,
    tunnels: detached.definition.tunnels.map(([u0, u1]) => ({ u0, u1 })),
    enabled: true,
  }));
  const physical = physicalSamples(detached.samples);
  const bridges = Object.freeze(detached.bridges.map((bridge) => Object.freeze({ ...bridge })));
  return Object.freeze({
    definition,
    samples: physical.samples,
    length: physical.length,
    halfWidth: detached.halfWidth,
    bridges,
    itemProgress: detached.itemProgress,
  });
}
